import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { CalendarIcon, PhoneIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { ClipLoader } from 'react-spinners';
import 'react-datepicker/dist/react-datepicker.css';
import { CONTACT_INFO } from '../constants/contact';
import useSEO from '../hooks/useSEO';
import { useSnackbar } from '../contexts/SnackbarContext';

interface BookingForm {
  fullName: string;
  phone: string;
  checkInDate: Date | null;
  checkOutDate: Date | null;
  adults: number;
  children: number;
  service: string;
  message: string;
}

export default function Booking() {
  // Structured data for SEO
  const bookingStructuredData = {
    "@context": "https://schema.org",
    "@type": "ReservationAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": "https://bethanchokstay.com/booking"
    },
    "result": {
      "@type": "LodgingReservation",
      "reservationFor": {
        "@type": "LodgingBusiness", 
        "name": "Bethanchok Homestay",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Bethanchok",
          "addressCountry": "Nepal"
        }
      }
    }
  };

  // SEO Hook - must be called at top level
  useSEO({
    title: "Book Your Stay | Bethanchok Homestay Reservation | Nepal Homestay Booking",
    description: "Book your authentic Nepal homestay experience at Bethanchok Homestay. Secure online booking for traditional village accommodation with mountain views and cultural experiences. Reserve now!",
    keywords: "book Bethanchok Homestay, Nepal homestay booking, reserve Nepal stay, Bethanchok reservation, Nepal village accommodation booking, authentic Nepal homestay reservation, mountain view accommodation Nepal",
    image: "https://bethanchokstay.com/images/booking/booking-banner.jpg",
    url: "https://bethanchokstay.com/booking",
    structuredData: bookingStructuredData
  });

  const [formData, setFormData] = useState<BookingForm>({
    fullName: '',
    phone: '',
    checkInDate: null,
    checkOutDate: null,
    adults: 2,
    children: 0,
    service: 'Accommodation',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const { showSnackbar } = useSnackbar();

  const services = [
    'Accommodation', 
    'Guided Trek', 
    'Local Experience', 
    'Cultural Tour', 
    'Family Stay'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Basic validation
    if (!formData.fullName || !formData.phone || !formData.checkInDate || !formData.checkOutDate) {
      showSnackbar('Please fill in all required fields.', 'error');
      setIsSubmitting(false);
      return;
    }

    if (formData.checkOutDate <= formData.checkInDate) {
      showSnackbar('Check-out date must be after check-in date.', 'error');
      setIsSubmitting(false);
      return;
    }

    // Format dates as "21 June 2025" in Asia/Kathmandu timezone
    const formatDate = (date: Date | null): string => {
      if (!date) return '';
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'Asia/Kathmandu'
      });
    };

    const bookingData = {
      fullName: formData.fullName,
      checkInDate: formatDate(formData.checkInDate),
      checkOutDate: formatDate(formData.checkOutDate),
      phone: formData.phone,
      adults: formData.adults,
      children: formData.children,
      service: formData.service,
      message: formData.message
    };

    try {
      const res = await fetch('/.netlify/functions/sendBookingEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });
      
      let responseData;
      try {
        responseData = await res.json();
      } catch (parseError) {
        responseData = { error: 'Invalid response format' };
      }
      
      if (res.ok) {
        showSnackbar('Booking request sent successfully!', 'success');
        setIsSubmitted(true);
      } else {
        console.error('Booking submission failed:', responseData);
        showSnackbar(responseData.error || 'Failed to send booking request', 'error');
      }
    } catch (err: any) {
      console.error('Network error:', err);
      showSnackbar('Network error - please check your connection', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted) {
    return (
      <div className="bg-white py-24 sm:py-32 page-transition">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <CheckCircleIcon className="mx-auto h-16 w-16 text-green-500 mb-6" />
            <h2 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
              Booking Request Sent!
            </h2>
            <p className="mt-6 text-lg leading-8 text-neutral-600">
              Thank you for your booking request. We have received your details and will 
              get back to you within 24 hours to confirm your reservation.
            </p>
            <div className="mt-8">
              <button
                onClick={() => {
                  setIsSubmitted(false);
                  setFormData({
                    fullName: '',
                    phone: '',
                    checkInDate: null,
                    checkOutDate: null,
                    adults: 2,
                    children: 0,
                    service: 'Accommodation',
                    message: ''
                  });
                }}
                className="rounded-lg bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 transition-colors duration-200"
              >
                Make Another Booking
              </button>
            </div>
          </div>
        </div>      </div>
    );
  }

  return (
    <div className="bg-white py-24 sm:py-32 page-transition">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl lg:text-center mb-16">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Reservation</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            Book Your Stay
          </p>
          <p className="mt-6 text-lg leading-8 text-neutral-600">
            Ready to experience authentic Nepali hospitality? Fill out the form below 
            and we'll get back to you with availability and booking confirmation.
          </p>
        </div>

        <div className="mx-auto max-w-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Guest Details */}
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium leading-6 text-neutral-900">
                Full Name *
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="fullName"
                  id="fullName"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="block w-full rounded-lg border-0 py-2 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium leading-6 text-neutral-900">
                Phone Number *
              </label>
              <div className="mt-2 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <PhoneIcon className="h-5 w-5 text-neutral-400" />
                </div>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="block w-full rounded-lg border-0 py-2 pl-10 pr-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  placeholder={CONTACT_INFO.phone}
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="checkInDate" className="block text-sm font-medium leading-6 text-neutral-900">
                  Check-in Date *
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <CalendarIcon className="h-5 w-5 text-neutral-400" />
                  </div>
                  <DatePicker
                    selected={formData.checkInDate}
                    onChange={(date) => setFormData({ ...formData, checkInDate: date })}
                    minDate={new Date()}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select check-in date"
                    className="block w-full rounded-lg border-0 py-2 pl-10 pr-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="checkOutDate" className="block text-sm font-medium leading-6 text-neutral-900">
                  Check-out Date *
                </label>
                <div className="mt-2 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                    <CalendarIcon className="h-5 w-5 text-neutral-400" />
                  </div>
                  <DatePicker
                    selected={formData.checkOutDate}
                    onChange={(date) => setFormData({ ...formData, checkOutDate: date })}
                    minDate={formData.checkInDate || new Date()}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select check-out date"
                    className="block w-full rounded-lg border-0 py-2 pl-10 pr-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Service Type and Guest Numbers */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div>
                <label htmlFor="service" className="block text-sm font-medium leading-6 text-neutral-900">
                  Service Type *
                </label>
                <div className="mt-2">
                  <select
                    name="service"
                    id="service"
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="block w-full rounded-lg border-0 py-2 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    required
                  >
                    {services.map((svc) => (
                      <option key={svc} value={svc}>{svc}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="adults" className="block text-sm font-medium leading-6 text-neutral-900">
                  Adults *
                </label>
                <div className="mt-2">
                  <select
                    name="adults"
                    id="adults"
                    value={formData.adults}
                    onChange={(e) => setFormData({ ...formData, adults: parseInt(e.target.value) })}
                    className="block w-full rounded-lg border-0 py-2 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                    required
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="children" className="block text-sm font-medium leading-6 text-neutral-900">
                  Children
                </label>
                <div className="mt-2">
                  <select
                    name="children"
                    id="children"
                    value={formData.children}
                    onChange={(e) => setFormData({ ...formData, children: parseInt(e.target.value) })}
                    className="block w-full rounded-lg border-0 py-2 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  >
                    {[0, 1, 2, 3, 4, 5, 6].map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Additional Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium leading-6 text-neutral-900">
                Additional Requests or Questions
              </label>
              <div className="mt-2">
                <textarea
                  name="message"
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="block w-full rounded-lg border-0 py-2 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 placeholder:text-neutral-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  placeholder="Any special requests, dietary requirements, or questions..."
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {isSubmitting ? (
                  <>
                    <ClipLoader color="white" size={16} className="mr-2" />
                    Sending Request...
                  </>
                ) : (
                  'Send Booking Request'
                )}
              </button>
            </div>
          </form>

          {/* Contact Info */}
          <div className="mt-12 p-6 bg-neutral-50 rounded-xl">
            <h3 className="text-lg font-semibold text-neutral-900 mb-4">
              Prefer to book directly?
            </h3>
            <div className="space-y-2 text-sm text-neutral-600">              <p>📞 Phone: {CONTACT_INFO.phone}</p>
              <p>📧 Email: {CONTACT_INFO.email}</p>
              <p>💬 WhatsApp: {CONTACT_INFO.whatsapp}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
